import { DateTime, DateTimeOptions } from 'luxon'
import { LocaleOptions } from 'luxon/src/datetime'
import { parseTimeInMinutes } from './numberHelper'

export type AnyDate = string | Date | DateTime

export function parseDate(isoDate: AnyDate, opts?: LocaleOptions): DateTime {
    const defaultOpts: LocaleOptions = { locale: DateTime.now().locale, ...opts }

    if (isoDate instanceof DateTime) {
        return isoDate.setLocale(defaultOpts.locale).startOf('day')
    } else if (isoDate instanceof Date) {
        return DateTime.fromJSDate(isoDate).setLocale(defaultOpts.locale).startOf('day')
    } else if (DateTime.fromSQL(isoDate).isValid) {
        return DateTime.fromSQL(isoDate).setLocale(defaultOpts.locale).startOf('day')
    } else if (DateTime.fromISO(isoDate).isValid) {
        return DateTime.fromISO(isoDate).setLocale(defaultOpts.locale).startOf('day')
    }

    return undefined
}

export function parseDateTime(isoDate: AnyDate, opts?: DateTimeOptions): DateTime {
    const serverOpts: DateTimeOptions = { zone: 'Europe/Amsterdam', locale: 'nl' }
    const defaultOpts: DateTimeOptions = { zone: DateTime.now().zone, locale: DateTime.now().locale, ...opts }

    if (isoDate instanceof DateTime) {
        return isoDate.setZone(defaultOpts.zone).setLocale(defaultOpts.locale)
    } else if (isoDate instanceof Date) {
        return DateTime.fromJSDate(isoDate).setZone(defaultOpts.zone).setLocale(defaultOpts.locale)
    } else if (DateTime.fromSQL(isoDate, serverOpts).isValid) {
        return DateTime.fromSQL(isoDate, serverOpts).setZone(defaultOpts.zone).setLocale(defaultOpts.locale)
    } else if (DateTime.fromISO(isoDate, serverOpts).isValid) {
        return DateTime.fromISO(isoDate, serverOpts).setZone(defaultOpts.zone).setLocale(defaultOpts.locale)
    }

    return undefined
}

export function compareDate(a: AnyDate, b: AnyDate): number {
    return parseDate(a || new Date()).toMillis() - parseDate(b || new Date()).toMillis()
}

export function compareDates(a: AnyDate, b: AnyDate, c: AnyDate, d: AnyDate): number {
    const startCompare = parseDate(a || '1970-01-01').toMillis() - parseDate(c || '1970-01-01').toMillis()
    const toCompare = parseDate(b || '3000-01-01').toMillis() - parseDate(d || '3000-01-01').toMillis()

    return startCompare === 0 ? toCompare : startCompare
}

export function compareDateTime(a: AnyDate, b: AnyDate): number {
    return parseDateTime(a || new Date()).toMillis() - parseDateTime(b || new Date()).toMillis()
}

export function formatIsoDate(isoDate: AnyDate): string {
    return parseDate(isoDate)?.toISODate() || ''
}

export function formatIsoDateTime(isoDate: AnyDate): string {
    return parseDateTime(isoDate)?.toISO() || ''
}

export function formatShortDate(isoDate: AnyDate): string {
    return parseDate(isoDate)?.toFormat('d LLL') || ''
}

export function formatShortNumericDate(isoDate: AnyDate): string {
    return parseDate(isoDate)?.toFormat('dd-LL') || ''
}

export function formatFullDate(isoDate: AnyDate): string {
    return parseDate(isoDate)?.toFormat('DD') || ''
}

export function formatFullNumericDate(isoDate: AnyDate): string {
    return parseDate(isoDate)?.toFormat('dd-LL-yyyy') || ''
}

export function formatShortDateTime(isoDate: AnyDate): string {
    return parseDateTime(isoDate)?.toFormat('d LLL T') || ''
}

export function formatFullDateTime(isoDate: AnyDate): string {
    return parseDateTime(isoDate)?.toFormat('DD T') || ''
}

export const formatDate = (date: AnyDate, opts?: LocaleOptions): string => parseDate(date, opts)?.toFormat(getDateFormat(date)) || ''
export const formatDateTime = (date: AnyDate, opts?: DateTimeOptions): string => parseDateTime(date, opts)?.toFormat(getTimeFormat(date)) || ''

export enum DateFormats {
    ThisYear = 'ccc dd-LL',
    Before = 'dd-LL-yyyy',
    Full = 'DDDD',
}

function getDateFormat(date: AnyDate): DateFormats {
    const currentDate = DateTime.now()
    const parsedDate = parseDate(date) || undefined

    if (inCurrentYear(currentDate, parsedDate)) {
        return DateFormats.ThisYear
    } else {
        return DateFormats.Before
    }
}

export enum TimeFormats {
    Today = 'T',
    CurrentWeek = 'ccc T',
    LastWeek = 'ccc dd-LL',
    Before = 'dd-LL-yyyy',
    ISO = 'dd-LL-yyyy T',
    Full = 'DDDD T',
}

function getTimeFormat(date: AnyDate): TimeFormats {
    const currentDate = DateTime.now()
    const parsedDate = parseDateTime(date) || undefined

    if (inToday(currentDate, parsedDate)) {
        return TimeFormats.Today
    } else if (inCurrentWeek(currentDate, parsedDate)) {
        return TimeFormats.CurrentWeek
    } else if (inLastWeek(currentDate, parsedDate)) {
        return TimeFormats.LastWeek
    } else {
        return TimeFormats.Before
    }
}

function inCurrentYear(currentDate: DateTime, parsedDate: DateTime) {
    return currentDate.year === parsedDate.year
}

function inLastWeek(currentDate: DateTime, parsedDate: DateTime) {
    // If current week is the first week of a year, check for year difference and week number of the parsed date
    if (currentDate.weekNumber === 1) {
        return currentDate.year - parsedDate.year === 1 && parsedDate.weekNumber === parsedDate.weeksInWeekYear
    }
    return currentDate.weekNumber - parsedDate.weekNumber === 1 && currentDate.year === parsedDate.year
}

function inCurrentWeek(currentDate: DateTime, parsedDate: DateTime) {
    return currentDate.weekNumber === parsedDate.weekNumber && currentDate.year === parsedDate.year
}

function inToday(currentDate: DateTime, parsedDate: DateTime) {
    return currentDate.day === parsedDate.day && currentDate.diff(parsedDate).as('days') < 1
}

export function formatDifferenceInTime(start: AnyDate, end: AnyDate, minimalValue: string = '00:01'): string {
    const startDate = parseDateTime(start)
    const endDate = parseDateTime(end)
    const diff = endDate.diff(startDate)
    return diff.toMillis() < parseTimeInMinutes(minimalValue) * 60000 ? minimalValue : diff.toFormat('h:mm')
}
